'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ShoppingCart, Menu, X } from 'lucide-react';
import SearchBar from './SearchBar';

// WhatsApp SVG icon component
function WhatsAppIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

interface HeaderProps {
  language: 'en' | 'ar';
  cartCount: number;
}

const WHATSAPP_GENERAL_URL = `https://wa.me/9647731885522?text=${encodeURIComponent('Hello PLC-IRAQ, I would like to request a quote for industrial automation parts.')}`;

export default function Header({ language, cartCount }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: language === 'ar' ? 'الماركات' : 'Brands', href: '#brands' },
    { label: language === 'ar' ? 'الفئات' : 'Categories', href: '#categories' },
    { label: language === 'ar' ? 'الخدمات' : 'Services', href: '#services' },
    { label: language === 'ar' ? 'عن الشركة' : 'About', href: '#about' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-header" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <a href="/" className="flex-shrink-0" aria-label="PLC-IRAQ Homepage">
            <Image
              src="/assets/images/PLC-Iraq_Logo-1788026633299.jpg"
              alt="PLC-IRAQ Logo"
              width={120}
              height={48}
              className="h-12 w-auto object-contain"
              priority
            />
          </a>

          {/* Search Bar */}
          <SearchBar language={language} />

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-slate-700 hover:text-iraqi-red transition-colors duration-200 whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href={WHATSAPP_GENERAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 bg-iraqi-red hover:bg-primary-dark text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-md whitespace-nowrap"
              aria-label="Request a quote via WhatsApp"
              title="Chat on WhatsApp to request a quote"
            >
              <WhatsAppIcon size={15} />
              {language === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
            </a>
            <Link
              href="/quote"
              className="relative p-2 text-slate-600 hover:text-iraqi-red transition-colors duration-200"
              aria-label={`Quote cart, ${cartCount} items`}
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-iraqi-red text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              className="lg:hidden p-2 text-slate-600 hover:text-iraqi-red transition-colors duration-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-3 pt-3 border-t border-slate-200 animate-fade-in">
            <nav className="flex flex-col gap-2" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium text-slate-700 hover:text-iraqi-red py-2 transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={WHATSAPP_GENERAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="mt-2 flex items-center justify-center gap-2 bg-iraqi-red text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-all duration-200"
                aria-label="Request a quote via WhatsApp"
                title="Chat on WhatsApp to request a quote"
              >
                <WhatsAppIcon size={15} />
                {language === 'ar' ? 'طلب عرض سعر' : 'Request a Quote'}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
